// French dictionary (Français). Translated from the canonical English
// dictionary in ./en.ts. Missing keys gracefully fall back to English at
// runtime.
import type { Dictionary } from "../index";

export const fr: Dictionary = {
  // ---- App / brand ----
  "app.name": "HomeBook",
  "app.tagline": "Réservations en famille",

  // ---- Common ----
  "common.signin": "Se connecter",
  "common.signup": "S'inscrire",
  "common.signout": "Se déconnecter",
  "common.cancel": "Annuler",
  "common.save": "Enregistrer",
  "common.delete": "Supprimer",
  "common.edit": "Modifier",
  "common.add": "Ajouter",
  "common.loading": "Chargement…",
  "common.search": "Rechercher",
  "common.filters": "Filtres",
  "common.clearAll": "Tout effacer",
  "common.none": "Aucun",
  "common.optional": "facultatif",
  "common.required": "requis",
  "common.total": "Total",
  "common.back": "Retour",
  "common.publish": "Publier",
  "common.hide": "Masquer",
  "common.allTypes": "Tous les types",
  "common.any": "Tous",
  "common.noImage": "Aucune image",

  // ---- Header / nav ----
  "nav.browse": "Découvrir",
  "nav.documentation": "Documentation",
  "nav.myTrips": "Mes voyages",
  "nav.hostPanel": "Espace hôte",
  "nav.admin": "Admin",
  "nav.listProperty": "+ Lister un bien",
  "nav.menu": "Menu",
  "nav.myTripsShort": "Mes voyages",
  "nav.hostPanelShort": "Espace hôte",
  "nav.adminShort": "Console admin",

  // ---- Roles ----
  "role.ADMIN": "Administrateur du site",
  "role.PRODUCT_ADMIN": "Hôte propriétaire",
  "role.USER": "Invité",

  // ---- Reservation status ----
  "status.PENDING": "En attente",
  "status.APPROVED": "Approuvé",
  "status.REJECTED": "Refusé",
  "status.CANCELLED": "Annulé",

  // ---- Booking types ----
  "booking.PRIVATE": "Privé",
  "booking.SHARED": "Partagé",
  "booking.PRIVATE.short": "Logement entier",
  "booking.SHARED.short": "Séjour partagé",
  "booking.PRIVATE.desc": "Réservez le logement entier pour vous. Tarif fixe par nuit ; les dates sont verrouillées une fois votre réservation approuvée.",
  "booking.SHARED.desc": "Plusieurs groupes peuvent séjourner en même temps. Le coût du logement est réparti entre tous les groupes occupants, au prorata par personne et par jour.",
  "booking.field.partySize": "Taille du groupe (nombre de personnes)",
  "booking.field.partySizeHint": "Combien de personnes dans votre groupe ?",
  "booking.privateTitle": "Réservation privée",
  "booking.onlySharedHint": "Ces dates comprennent déjà un séjour partagé : cette réservation ne peut être que partagée.",
  "booking.onlyPrivateHint": "Ces dates chevauchent un séjour privé : toute la maison est nécessaire.",
  "booking.sharedTitle": "Réservation partagée — coût réparti au prorata",
  "detail.privateBlockHint": "Ces dates sont couvertes par une réservation privée (exclusive) approuvée — choisissez d'autres dates.",
  "detail.bookedLegend": "Rouge = séjour privé exclusif (toute la maison) · Violet = séjour partagé (au prorata, nombre d'invités indiqué).",
  "booking.coOccupants": "Co-occupants sur ces dates",
  "booking.noCoOccupants": "Aucun autre groupe approuvé sur ces dates pour l'instant — vous paieriez le tarif complet par nuit si vous étiez approuvé seul.",
  "booking.partyN": "{count} {noun}",
  "booking.partyNoun": "personne",
  "partyNounPlural": "personnes",
  "booking.occupantsToday": "{count} personnes réservées",
  "booking.yourShare": "Votre part estimée",
  "booking.yourShareHint": "Le prix final est recalculé lors de l'approbation, en fonction de tous les co-occupants approuvés.",
  "booking.perDay": "par jour",
  "booking.split": "{share} ({party} sur {total} personnes)",
  "booking.capacityExceeded": "Ces dates ont déjà atteint la capacité maximale.",
  "booking.immutableLocked": "Le type de réservation est verrouillé une fois des réservations existent.",

  // ---- Property types ----
  "property.HOUSE": "Maison",
  "property.APARTMENT": "Appartement",
  "property.VILLA": "Villa",
  "property.CHALET": "Chalet",
  "property.STUDIO": "Studio",
  "property.COTTAGE": "Chaumière",

  // ---- Auth modal ----
  "auth.welcome": "Bienvenue sur HomeBook",
  "auth.subtitle": "Connectez-vous pour réserver un séjour, ou créez un compte pour commencer.",
  "auth.tabSignin": "Se connecter",
  "auth.tabRegister": "Créer un compte",
  "auth.demoHint": "Démo : admin / admin123 · guest / guest123",
  "auth.signingIn": "Connexion…",
  "auth.creating": "Création du compte…",
  "auth.createAccount": "Créer un compte",

  // Auth fields
  "auth.field.username": "Nom d'utilisateur",
  "auth.field.usernamePlaceholder": "jane_doe",
  "auth.field.usernameHint": "3 à 30 caractères, lettres/chiffres/_/-, doit commencer par une lettre.",
  "auth.field.firstName": "Prénom",
  "auth.field.lastName": "Nom",
  "auth.field.email": "E-mail",
  "auth.field.emailPlaceholder": "you@example.com",
  "auth.field.phone": "Téléphone (facultatif)",
  "auth.field.phonePlaceholder": "+1-555-0100",
  "auth.field.password": "Mot de passe",
  "auth.field.passwordPlaceholder": "••••••••",
  "auth.field.passwordHint": "Au moins 6 caractères",

  // Username availability
  "username.checking": "Vérification de la disponibilité…",
  "username.available": "Nom d'utilisateur disponible",
  "username.taken": "Ce nom d'utilisateur est déjà pris",
  "username.invalid": "Le nom d'utilisateur doit comporter 3 à 30 caractères, commencer par une lettre, et ne contenir que des lettres, des chiffres, _ ou -.",
  "username.tooShort": "Nom d'utilisateur trop court",

  // ---- Browse view ----
  "browse.heroBadge": "Réservations en famille",
  "browse.heroTitle": "Réservez une maison familiale, un chalet ou un appartement",
  "browse.heroSubtitle": "Les propriétaires publient leurs biens avec photos et dates de disponibilité. Les invités demandent des réservations. Les hôtes et l'administrateur du site valident chaque réservation — une fois approuvée, les dates sont verrouillées.",
  "browse.searchPlaceholder": "Rechercher par titre, lieu ou description…",
  "browse.resultsCount": "{count} {plural} disponible(s)",
  "browse.resultSingular": "propriété",
  "browse.resultPlural": "propriétés",
  "browse.loadingResults": "Chargement…",
  "browse.noResultsTitle": "Aucun bien ne correspond à votre recherche",
  "browse.noResultsBody": "Essayez de retirer des filtres ou de chercher d'autres dates.",
  "browse.signInToReserve": "Connectez-vous pour réserver un séjour.",
  "browse.activeFilters": "Filtres actifs :",
  "browse.guestsChip": "{count}+ invités",

  // Filters
  "filter.propertyType": "Type de bien",
  "filter.guests": "Invités",
  "filter.checkIn": "Arrivée",
  "filter.checkOut": "Départ",

  // ---- Product card ----
  "card.perNight": "/nuit",
  "card.bedrooms": "{count} chambre{plural}",
  "card.bathrooms": "{count} salle de bain{plural}",
  "card.guests": "{count} invités max",

  // ---- Product detail ----
  "detail.backToAll": "Retour à tous les biens",
  "layers.title": "Qui voit quoi",
  "layers.axisTop": "plus d'accès",
  "layers.axisBottom": "moins d'accès",
  "layers.adminScope": "tout, partout",
  "layers.ownerScope": "contrôle total de leurs maisons",
  "layers.staffScope": "une maison assignée : réservations + factures",
  "layers.userScope": "ses réservations + les factures assignées",
  "layers.chHouses": "Maisons",
  "layers.chBookings": "Réservations",
  "layers.chBills": "Factures",
  "layers.chUsers": "Utilisateurs",
  "layers.chSettings": "Réglages",
  "layers.chStaff": "Personnel",
  "layers.chMyTrips": "Mes voyages",
  "layers.chAssignedBills": "Factures assignées",
  "layers.legendAdmin": "Tout, toutes les maisons",
  "layers.legendOwner": "Contrôle total de leurs maisons",
  "layers.legendStaff": "Une maison assignée : réservations + factures, jamais la gestion de la maison",
  "layers.legendUser": "Ses voyages + les factures qui lui sont assignées",
  "pd.manage": "Gérer cette maison",
  "pd.changePhoto": "Changer cette photo",
"pd.addPhoto": "Ajouter une photo",
"pd.photoUpdated": "Photo mise à jour",
  "pd.staff": "Personnel de la maison",
"pd.staffHint": "Les modérateurs assignés peuvent valider les réservations et gérer les factures de cette maison uniquement. Ils ne voient rien d'autre.",
"pd.addStaff": "Ajouter un modérateur…",
"pd.staffEmpty": "Aucun modérateur assigné.",
"pd.staffRemove": "Retirer de cette maison",
"pd.staffAdded": "Modérateur ajouté à cette maison",
"pd.staffRemoved": "Modérateur retiré",
  "detail.about": "À propos de ce bien",
  "detail.amenities": "Équipements",
  "detail.availability": "Périodes de disponibilité",
  "detail.noAvailability": "Aucune période de disponibilité publiée pour le moment.",
  "detail.alreadyBooked": "Déjà réservé",
  "detail.perNight": "par nuit",
  "detail.checkIn": "Arrivée",
  "detail.checkOut": "Départ",
  "detail.guestName": "Nom de l'invité",
  "detail.guestEmail": "E-mail",
  "detail.guestPhone": "Téléphone (facultatif)",
  "detail.notes": "Notes (facultatif)",
  "detail.notesPlaceholder": "Nombre d'enfants, heure d'arrivée, demandes particulières…",
  "detail.priceRow": "{price} × {nights} nuit{plural}",
  "detail.requestReservation": "Demander une réservation",
  "detail.signInToReserve": "Se connecter pour réserver",
  "detail.requesting": "Envoi de la demande…",
  "detail.requestHelpText": "Votre demande est envoyée à l'hôte pour validation. Les dates approuvées deviennent indisponibles pour les autres.",

  // ---- My trips ----
  "trips.title": "Mes voyages",
  "trips.subtitle": "Vos demandes de réservation et leur statut. Les hôtes valident chaque demande avant confirmation.",
  "trips.upcoming": "À venir",
  "trips.history": "Historique",
  "trips.signInTitle": "Connectez-vous pour voir vos voyages",
  "trips.signInBody": "Vos demandes de réservation et leur statut de validation s'affichent ici.",
  "trips.emptyTitle": "Aucune réservation pour le moment",
  "trips.emptyBody": "Parcourez les biens et demandez un séjour pour commencer.",
  "trips.cancelRequest": "Annuler la demande",
  "trips.validatedBy": "Validé par {name}",

  // ---- Host panel ----
  "host.title": "Espace hôte",
  "host.subtitleAdmin": "Administrateur du site — gérez tous les biens et validez n'importe quelle réservation.",
  "host.subtitleOwner": "Gérez vos biens et validez les demandes de réservation des invités.",
  "host.tabProperties": "Biens",
  "host.tabReservations": "Réservations",
  "host.pendingCount": "{count} en attente",
  "host.listProperty": "Lister un bien",
  "host.noPropertiesTitle": "Aucun bien pour le moment",
  "host.noPropertiesBody": "Créez votre premier bien pour commencer à recevoir des réservations.",
  "host.edit": "Modifier",
  "host.availability": "Disponibilité",
  "host.photos": "Photos ({count})",
  "host.delete": "Supprimer",
  "host.published": "Publié",
  "host.hidden": "Masqué",
  "host.noAvailabilitySet": "Aucune disponibilité définie",
  "host.availWindows": "{count} fenêtre{plural} de disponibilité",
  "host.awaitingDecision": "En attente de votre décision",
  "host.noPending": "Aucune demande en attente pour le moment.",
  "host.allRequests": "Toutes les demandes",
  "host.noOther": "Aucune autre réservation.",
  "host.approve": "Approuver",
  "host.decline": "Refuser",
  "host.deleteTitle": "Supprimer ce bien ?",
  "host.deleteBody": "Cela supprimera le bien, ses photos, ses périodes de disponibilité et les réservations associées. Cette action est irréversible.",

  // ---- Availability modal ----
  "avail.title": "Gérer la disponibilité",
  "avail.description": "Ajoutez les plages de dates pendant lesquelles les invités peuvent réserver ce bien. Les réservations doivent s'inscrire entièrement dans une seule plage.",
  "avail.from": "Disponible à partir du",
  "avail.to": "Disponible jusqu'au",
  "avail.addWindow": "Ajouter une plage",
  "avail.current": "Périodes actuelles ({count})",
  "avail.empty": "Aucune disponibilité pour le moment. Ajoutez une plage ci-dessus pour que les invités puissent réserver.",

  // ---- Image manager ----
  "images.title": "Gérer les photos",
  "images.description": "Téléversez les photos de votre bien (JPG, PNG, WebP — 8 Mo max). La première photo est utilisée comme image de couverture.",
  "images.caption": "Légende (facultatif)",
  "images.photos": "Photos ({count})",
  "images.empty": "Aucune photo pour le moment.",
  "images.cover": "Couverture",

  // ---- Product editor ----
  "productEditor.newTitle": "Lister un nouveau bien",
  "productEditor.editTitle": "Modifier le bien",
  "productEditor.description": "Remplissez les détails. Vous pourrez ajouter des photos et des dates de disponibilité après l'enregistrement.",
  "productEditor.title": "Titre",
  "productEditor.titlePlaceholder": "The Martin Family House",
  "productEditor.descriptionLabel": "Description",
  "productEditor.location": "Lieu",
  "productEditor.locationPlaceholder": "12 Maple Lane, Greenwood Hills",
  "productEditor.type": "Type de bien",
  "productEditor.bookingType": "Type de réservation",
  "productEditor.bookingTypeHint": "Privé = logement entier, tarif fixe, dates verrouillées à l'approbation. Partagé = plusieurs groupes, coût réparti au prorata par personne et par jour. Impossible à modifier une fois des réservations existent.",
  "productEditor.price": "Prix / nuit ($)",
  "productEditor.bedrooms": "Chambres",
  "productEditor.bathrooms": "Salles de bain",
  "productEditor.maxGuests": "Invités max",
  "productEditor.amenities": "Équipements (séparés par des virgules)",
  "productEditor.amenitiesPlaceholder": "WiFi,Parking,Jardin,Cheminée",
  "productEditor.saveChanges": "Enregistrer les modifications",
  "productEditor.create": "Créer le bien",
  "productEditor.saving": "Enregistrement…",

  // ---- Admin console ----
  "admin.title": "Console admin",
  "admin.subtitle": "Outils d'administration du site — utilisateurs, niveaux d'accès, contenu & vue d'ensemble de la validation.",
  "admin.tabOverview": "Vue d'ensemble",
  "admin.tabUsers": "Utilisateurs",
  "admin.tabReservations": "Réservations",
  "admin.statUsers": "Total utilisateurs",
  "admin.statProperties": "Biens",
  "admin.statReservations": "Réservations",
  "admin.statRevenue": "Revenus (approuvés)",
  "admin.resByStatus": "Réservations par statut",
  "admin.usersByRole": "Utilisateurs par niveau d'accès",
  "admin.addUser": "Ajouter un utilisateur",
  "admin.colName": "Nom",
  "admin.colUsername": "Nom d'utilisateur",
  "admin.colEmail": "E-mail",
  "admin.colRole": "Rôle",
  "admin.colGroup": "Groupe",
"admin.allRoles": "Tous les rôles",
"admin.allGroups": "Tous les groupes",
"admin.unassigned": "Sans groupe",
  "admin.colProperties": "Biens",
  "admin.colReservations": "Réservations",
  "admin.colStatus": "Statut",
  "admin.colActions": "Actions",
  "admin.active": "Actif",
  "admin.disabled": "Désactivé",
  "admin.deleteUserTitle": "Supprimer cet utilisateur ?",
  "admin.deleteUserBody": "Cela supprime définitivement le compte. Ses séjours restent dans les calendriers comme historique, et ses factures restent sans assigné.",
  "admin.validateAny": "Validez n'importe quelle réservation sur la plateforme.",
  "admin.noPending": "Aucune réservation en attente à valider.",
  "admin.recentDecisions": "Décisions récentes",

  // ---- User editor ----
  "userEditor.newTitle": "Créer un utilisateur",
  "userEditor.editTitle": "Modifier l'utilisateur",
  "userEditor.description": "Gérez le profil, le rôle et le niveau d'accès d'un utilisateur.",
  "userEditor.username": "Nom d'utilisateur",
  "userEditor.firstName": "Prénom",
  "userEditor.lastName": "Nom",
  "userEditor.email": "E-mail",
  "userEditor.phone": "Téléphone (facultatif)",
  "userEditor.role": "Rôle / niveau d'accès",
  "userEditor.active": "Compte actif",
  "userEditor.password": "Mot de passe",
  "userEditor.passwordKeep": "(laisser vide pour conserver l'actuel)",
  "userEditor.passwordSet": "Mot de passe (vide = généré automatiquement)",
  "userEditor.passwordPlaceholder": "••••••••",
  "userEditor.saveChanges": "Enregistrer les modifications",
  "userEditor.create": "Créer l'utilisateur",
  "userEditor.saving": "Enregistrement…",

  // ---- Language switcher ----
  "lang.label": "Langue",
  "lang.changed": "Langue mise à jour",

  // ---- Toasts ----
  "toast.welcomeBack": "Bon retour, {name} !",
  "toast.accountCreated": "Compte créé. Bienvenue, {name} !",
  "toast.signedOut": "Déconnecté",
  "toast.reservationRequested": "Réservation demandée ! L'hôte la validera prochainement.",
  "toast.reservationCancelled": "Réservation annulée",
  "toast.propertyCreated": "Bien créé. Ajoutez les photos et la disponibilité ensuite.",
  "toast.propertyUpdated": "Bien mis à jour",
  "toast.propertyDeleted": "Bien supprimé",
  "toast.propertyHidden": "Bien masqué",
  "toast.propertyPublished": "Bien publié",
  "toast.availAdded": "Période de disponibilité ajoutée",
  "toast.availRemoved": "Supprimé",
  "toast.photoAdded": "Photo ajoutée",
  "toast.photoRemoved": "Photo supprimée",
  "toast.uploadFailed": "Échec du téléversement",
  "toast.userCreated": "Utilisateur créé",
  "toast.userUpdated": "Utilisateur mis à jour",
  "toast.userDeleted": "Utilisateur supprimé",
  "toast.userEnabled": "Utilisateur activé",
  "toast.userDisabled": "Utilisateur désactivé",
  "toast.reservationApproved": "Réservation approuvée",
  "toast.reservationRejected": "Réservation refusée",
  "toast.approved": "Approuvé",
  "toast.rejected": "Refusé",
  "toast.actionFailed": "Échec de l'action",
  "toast.couldNotCancel": "Impossible d'annuler",
  "toast.couldNotUpdate": "Impossible de mettre à jour",
  "toast.couldNotDelete": "Impossible de supprimer",
  "toast.couldNotSave": "Impossible d'enregistrer",
  "toast.couldNotLoad": "Impossible de charger",
  "toast.couldNotLoadUser": "Impossible de charger l'utilisateur",
  "toast.couldNotRemove": "Impossible de supprimer",
  "toast.couldNotCreate": "Impossible de créer la réservation",
  "toast.pleaseChooseDates": "Veuillez choisir les dates d'arrivée et de départ",

  // ---- Footer ----
  "footer.tagline": "Une plateforme de réservation pour maisons familiales, chalets et appartements. Les propriétaires publient, les invités réservent, les administrateurs valident chaque séjour.",
  "footer.explore": "Explorer",
  "footer.allProperties": "Tous les biens",
  "footer.publicPages": "Pages publiques des maisons",
  "footer.myTrips": "Mes voyages",
  "footer.forHosts": "Pour les hôtes",
  "footer.host1": "Lister un bien avec photos & dates",
  "footer.host2": "Gérer les périodes de disponibilité",
  "footer.host3": "Approuver ou refuser les demandes de réservation",
  "footer.roadmap": "Feuille de route",
  "footer.roadmap1": "Applications iOS & Android",
  "footer.roadmap2": "Notifications push par groupe WhatsApp",
  "footer.roadmap3": "Synchronisation du calendrier & paiements",
  "footer.rights": "Tous droits réservés.",
  "footer.builtWith": "Construit avec Next.js, Prisma & mots de passe salés et hachés.",

  // ---- Misc ----
  "night": "nuit",
  "nights": "nuits",
  "guests": "invités",
  "properties": "biens",

  // ---- Auth (account pending) ----
  "auth.accountPending": "Votre compte est en attente de validation. Un modérateur le validera prochainement — vous pourrez réserver une fois approuvé.",

  // ---- Roles (incl. moderator) ----
  "role.MODERATOR": "Modérateur",

  // ---- User groups ----
  "group.FAMILY": "Famille",
  "group.GUEST": "Invité",
  "group.label": "Groupe",
  "group.none": "Non assigné",
  "group.familyDesc": "Famille partageant la maison familiale — bénéficient d'une remise famille et peuvent être enregistrés comme payeurs de dépenses.",
  "group.guestDesc": "Locataires de vacances extérieurs — paient le tarif saisonnier standard.",

  // ---- Validation ----
  "nav.moderator": "Modération",
  "nav.expenses": "Dépenses",
  "nav.dashboard": "Tableau de bord",
  "nav.notifications": "Notifications",
  "mod.title": "Panneau de modération",
  "mod.subtitle": "Tout ce qui attend une décision : valider les nouveaux comptes, approuver les demandes de réservation et tenir le répertoire des membres à jour.",
  "mod.pending": "En attente de validation",
  "mod.pendingCount": "{count} en attente",
  "mod.noPending": "Aucun compte en attente de validation.",
  "mod.validateAs": "Valider en tant que",
  "mod.validateFamily": "Valider comme Famille",
  "mod.validateGuest": "Valider comme Invité",
  "mod.validated": "Validé",
  "mod.notValidated": "Non validé",
  "mod.signedUp": "Inscrit le",
  "mod.validatedBy": "Validé par {name}",
  "toast.userValidated": "Compte validé",
  "toast.ownershipTransferred": "Propriété transférée",
  "toast.roleUpdated": "Rôle mis à jour",

  // ---- Notifications ----
  "notif.title": "Canaux de notification",
  "notif.subtitle": "Où les notifications d'inscription / réservation / dépense sont envoyées. Les fournisseurs e-mail et WhatsApp sont interchangeables (journaux par défaut ; remplacez-les par un vrai SMTP / WhatsApp Business API en production).",
  "notif.addEmail": "Ajouter un e-mail",
  "notif.addWhatsapp": "Ajouter WhatsApp",
  "notif.target": "Cible (e-mail ou téléphone)",
  "notif.label": "Libellé (facultatif)",
  "notif.kind": "Type",
  "notif.EMAIL": "E-mail",
  "notif.WHATSAPP": "WhatsApp",
  "notif.active": "Actif",
  "notif.inactive": "Inactif",
  "notif.history": "Historique des envois",
  "notif.noChannels": "Aucun canal configuré. Ajoutez une cible e-mail ou WhatsApp ci-dessus.",
  "notif.noHistory": "Aucune notification envoyée pour le moment.",
  "toast.channelAdded": "Canal ajouté",
  "toast.channelRemoved": "Canal supprimé",
  "toast.channelUpdated": "Canal mis à jour",

  // ---- Season pricing ----
  "season.highSeasonRate": "Tarif haute saison ($/nuit)",
  "season.highSeasonRateHint": "Appliqué aux jours inclus dans une plage haute saison. Laissez 0 pour utiliser le tarif de base partout.",
  "season.familyDiscount": "Remise famille (%)",
  "season.familyDiscountHint": "Pourcentage de réduction sur le tarif par nuit pour les utilisateurs du groupe FAMILLE. 0 = aucune remise.",
  "season.ranges": "Plages haute saison",
  "season.addRange": "Ajouter une plage haute saison",
  "season.rangeName": "Nom de la plage",
  "season.from": "Du",
  "season.to": "Au",
  "season.current": "Plages haute saison actuelles ({count})",
  "season.empty": "Aucune plage haute saison — chaque jour utilise le tarif de base.",
  "season.highSeason": "Haute saison",
  "season.lowSeason": "Basse saison",
  "season.highSeasonBadge": "Haute saison {rate}",
  "toast.seasonAdded": "Plage haute saison ajoutée",
  "toast.seasonRemoved": "Plage haute saison supprimée",
  "detail.seasonNote": "Les tarifs saisonniers s'appliquent par jour ; la remise famille est appliquée pour les utilisateurs du groupe FAMILLE.",

  // ---- Expenses ----
  "expenses.title": "Dépenses & frais",
  "expenses.subtitle": "Suivez les frais fixes (taxes, électricité, gaz, internet, abonnements) et les frais ponctuels (entretien). Enregistrez qui a payé chaque facture.",
  "expenses.tabPending": "En attente ({count})",
  "expenses.tabPaid": "Payées ({count})",
  "expenses.tabDueSoon": "À payer sous peu ({count})",
  "expenses.badgeWrittenOff": "Annulée",
  "expenses.add": "Ajouter une dépense",
  "expenses.product": "Bien",
  "expenses.type": "Type",
  "expenses.typeFixed": "Fixe (récurrent)",
  "expenses.typePunctual": "Ponctuel (unique)",
  "expenses.category": "Catégorie",
  "expenses.cat.TAXES": "Taxes (logement et terrain)",
  "expenses.cat.ELECTRICITY": "Électricité",
  "expenses.cat.GAS": "Gaz",
  "expenses.cat.INTERNET": "Abonnement internet",
  "expenses.cat.SUBSCRIPTION": "Abonnement service",
  "expenses.cat.MAINTENANCE": "Entretien / réparations",
  "expenses.cat.CUSTOM": "Personnalisé",
  "expenses.customLabel": "Libellé personnalisé",
  "expenses.description": "Description",
  "expenses.amount": "Montant ($)",
  "expenses.dueDate": "Date d'échéance",
  "expenses.periodStart": "Début de période",
  "expenses.periodEnd": "Fin de période",
  "expenses.status": "Statut",
  "expenses.PENDING": "En attente",
  "expenses.PAID": "Payée",
  "expenses.markPaid": "Enregistrer le paiement",
  "expenses.paidBy": "Payé par",
  "expenses.paidDate": "Payé le",
  "expenses.paymentMethod": "Moyen de paiement",
  "expenses.method.CASH": "Espèces",
  "expenses.method.BANK_TRANSFER": "Virement bancaire",
  "expenses.method.CARD": "Carte",
  "expenses.method.OTHER": "Autre",
  "expenses.selectPayer": "Sélectionner un payeur famille",
  "expenses.noFamilyPayers": "Aucun utilisateur FAMILLE validé pour le moment. Validez d'abord un utilisateur comme Famille.",
  "expenses.noExpenses": "Aucune dépense pour le moment.",
  "expenses.noPaid": "Aucun paiement enregistré pour le moment.",
  "expenses.totalPending": "Total en attente",
  "expenses.totalPaid": "Total payé",
  "toast.expenseAdded": "Dépense ajoutée",
  "toast.expenseUpdated": "Dépense mise à jour",
  "toast.expenseDeleted": "Dépense supprimée",
  "toast.expensePaid": "Paiement enregistré",
  "toast.onlyFamilyCanPay": "Seuls les utilisateurs du groupe FAMILLE peuvent être enregistrés comme payeurs",

  // ---- Dashboard ----
  "dash.title": "Tableau de bord",
  "dash.subtitle": "Qui a payé quoi et quand. Contributions familiales, répartition des dépenses et éléments en attente en un coup d'œil.",
  "dash.summaryTotalPaid": "Total payé",
  "dash.summaryTotalPending": "Total en attente",
  "dash.summaryPayments": "Paiements enregistrés",
  "dash.summaryPendingUsers": "Utilisateurs en attente de validation",
  "dash.byPayer": "Payé par membre de la famille",
  "dash.byCategory": "Payé par catégorie",
  "dash.recentPayments": "Paiements récents (qui a payé quoi et quand)",
  "dash.pendingExpenses": "Dépenses en attente",
  "dash.noPayments": "Aucun paiement enregistré pour le moment.",
  "dash.noPendingExpenses": "Aucune dépense en attente. Tout est payé !",
  "dash.paidOn": "Payé le",
  "dash.by": "par",
  "dash.noContributions": "Aucune contribution enregistrée pour le moment.",

  // ---- Admin role-preview (impersonation) ----
  "preview.as": "Prévisualiser en tant qu'utilisateur",
  "preview.title": "Prévisualiser en tant qu'utilisateur",
  "preview.hint": "Parcourez l'application avec le rôle et les permissions d'un autre utilisateur. Votre session admin reste active en arrière-plan.",
  "preview.preview": "Prévisualiser",
  "preview.exit": "Quitter la prévisualisation",
  "preview.exited": "Prévisualisation terminée — vous êtes à nouveau admin.",
  "preview.started": "Prévisualisation en tant que {name}",
  "preview.banner": "Prévisualisation en tant que {name} ({role})",

  // ---- User editor: group + validated ----
  "userEditor.group": "Groupe d'utilisateur",
  "userEditor.validated": "Compte validé",
  "userEditor.validatedHint": "Les utilisateurs validés peuvent effectuer des réservations.",
  "userEditor.generate": "Générer",
"userEditor.createdTitle": "Utilisateur créé",
"userEditor.createdHint": "Copiez l'identifiant et le mot de passe maintenant — il ne sera plus affiché.",
"userEditor.copy": "Copier",
"userEditor.copied": "Copié",
"userEditor.done": "Terminé",
"userEditor.optional": "(facultatif)",
"userEditor.maskedNote":
  "Les données personnelles sont affichées masquées pour la confidentialité. Retapez un champ pour le modifier.",

  // ---- Dépenses : vue d'ensemble, dettes & factures ----
  "expenses.cardToPay": "À payer",
  "expenses.cardOverdue": "En retard",
  "expenses.cardDueSoon": "Échéance proche (30 jours)",
  "expenses.tabAll": "Toutes",
  "expenses.tabToPay": "À payer ({count})",
  "expenses.tabDebts": "Dettes ({count})",
  "expenses.tabWrittenOff": "Annulées ({count})",
  "expenses.badgePending": "À payer",
  "expenses.badgePaid": "Payée",
  "expenses.paidLateBadge": "Payée en retard",
  "expenses.dayLate": "1 jour de retard",
  "expenses.daysLate": "{count} jours de retard",
  "expenses.assignedTo": "Assignée à",
  "expenses.assignToHint": "Le membre de la Famille responsable de cette dépense",
  "expenses.unassigned": "Non assignée",
  "expenses.writeOff": "Annuler la dette",
  "expenses.writeOffTitle": "Annuler cette dette",
  "expenses.writeOffDesc": "Cette dépense reste dans l'historique mais n'est plus comptée comme due.",
  "expenses.writeOffNote": "Motif (facultatif)",
  "expenses.writeOffConfirm": "Annuler la dette",
  "expenses.debtsSummary": "Dettes en cours par membre",
  "expenses.bills": "{count} facture(s)",
  "expenses.noWrittenOff": "Aucune dette annulée.",
  "expenses.noDebts": "Aucune facture en retard — tout est à jour.",
  "expenses.invoice": "Facture",
  "toast.writeOffDone": "Dette annulée",
  "invoice.title": "Facture",
  "invoice.number": "Facture n°",
  "invoice.issueDate": "Date d'émission",
  "invoice.dueDate": "Échéance",
  "invoice.from": "Émetteur",
  "invoice.billTo": "Facturé à",
  "invoice.description": "Description",
  "invoice.category": "Catégorie",
  "invoice.period": "Période",
  "invoice.total": "Total",
  "invoice.status": "Statut",
  "invoice.status.PENDING": "À payer",
  "invoice.status.OVERDUE": "En retard",
  "invoice.status.PAID": "Payée",
  "invoice.status.WRITTEN_OFF": "Annulée",
  "invoice.paidOn": "Payée le",
  "invoice.notes": "Notes",
  "invoice.print": "Imprimer",
  "invoice.back": "Retour aux dépenses",
  "invoice.notFound": "Facture introuvable ou accès refusé.",

  // ---- Familles & groupes ----
  "mod.tabValidation": "Validation",
  "mod.tabGroups": "Familles & groupes",
  "mod.tabOwnership": "Propriété",
  "mod.tabUsers": "Utilisateurs",
  "mod.tabReview": "À valider",
"mod.reviewEmpty": "Rien en attente — tout est clair.",
"mod.queueBookings": "Demandes de réservation",
"mod.queueAccounts": "Comptes",
  "mod.tabBookings": "Demandes de réservation",
"mod.noPendingBookings": "Aucune demande en attente.",
"mod.bookingsHint": "Approuvez ou rejetez les demandes ci-dessous. Approuver réserve les dates ; rejeter les libère.",
"permissions.feature.bookings.validate": "Approuver / rejeter les demandes de réservation",
"admin.noUsers": "Aucun utilisateur ne correspond aux filtres.",
"admin.disable": "Désactiver le compte",
"admin.enable": "Activer le compte",
"invitations.family": "Famille (facultatif)",
"invitations.noFamily": "Aucune famille",
"invitations.inviteAnother": "Inviter un autre invité",
"mod.ownershipHint": "Donnez une maison à un membre. Le nouveau propriétaire en obtient le contrôle total et peut y assigner son personnel.",
"mod.currentOwner": "Propriétaire",
"mod.transferSelect": "Choisir le nouveau propriétaire…",
"mod.transferApply": "Transférer",
"mod.transferConfirm": "Transférer '{house}' à {name} ?",
"mod.noHouses": "Aucune maison pour le moment.",
  "mod.chooseFamily": "Quelle famille ?",
  "mod.validateInto": "Valider comme {group}",
  "groups.subtitle": "Les maisons appartiennent à des familles. Les membres d'une famille bénéficient de la remise famille sur les biens de leur famille.",
  "groups.create": "Créer une famille",
  "groups.name": "Nom de la famille",
  "groups.namePlaceholder": "ex. La famille Martin",
  "groups.members": "{count} membre(s)",
  "groups.properties": "{count} bien(s)",
  "groups.addMember": "Ajouter un membre",
  "groups.removeMember": "Retirer de la famille",
  "groups.removeConfirm": "Retirer {name} de la famille ? Il/Elle devient invité(e).",
  "groups.rename": "Renommer",
  "groups.delete": "Supprimer la famille",
  "groups.deleteConfirm": "Supprimer la famille vide \"{name}\" ?",
  "groups.guests": "Invités",
  "groups.guestsHint": "Utilisateurs validés hors de toute famille.",
  "groups.noFamilies": "Aucune famille — créez-en une pour regrouper les membres.",
  "groups.candidatesEmpty": "Aucun utilisateur validé disponible.",
  "groups.unassignedFam": "Membres de famille sans famille",
  "groups.memberAdded": "{name} a rejoint {family}",
  "groups.memberRemoved": "{name} a quitté la famille",
  "groups.familyCreated": "Famille créée",
  "groups.familyRenamed": "Famille renommée",
  "groups.familyDeleted": "Famille supprimée",

  // ---- Calendrier du tableau de bord ----
  "calendar.title": "Disponibilités & réservations",
  "calendar.chooseProperty": "Choisir un bien",
  "calendar.legendAvailable": "Disponible",
  "calendar.legendBookedPrivate": "Réservé — privé",
  "calendar.legendBookedShared": "Réservé — partagé",
  "calendar.legendPending": "Demande en attente",
  "calendar.legendMixed": "Réservé + en attente",
  "calendar.legendOff": "Indisponible / passé",
  "calendar.pendingShort": "En attente",
  "calendar.mixedShort": "Réservé + en attente",
  "calendar.availableShort": "Disponible",
  "calendar.passedShort": "Passé",
  "calendar.noProperties": "Aucun bien actif.",
  "calendar.viewMonth": "Mois",
  "calendar.viewYear": "Année",
  "calendar.typeHint": "Type de réservation de ce bien : privé = exclusif, partagé = au prorata.",
  "calendar.bookMonth": "Ajouter une réservation dans ce mois",
  "calendar.daySuffix": "j",
  "calendar.daySuffixP": "d",
  "calendar.dayDetails": "Réservations du {date}",
  "calendar.exclusiveNote": "Cette journée est réservée en exclusivité par un séjour privé.",
  "calendar.addHere": "Ajouter une réservation ce jour",
  "calendar.status.APPROVED": "Confirmée",
  "calendar.status.PENDING": "En attente",
  "calendar.typeBlockedHint": "Ce type de réservation n'est pas disponible pour les dates sélectionnées — une réservation approuvée les occupe. Choisissez l'autre type ou d'autres dates.",
  "calendar.cancelBooking": "Annuler cette réservation",
  "calendar.cancelTitle": "Annuler cette réservation ?",
  "calendar.cancelAck": "La confirmation annulera cette réservation et libérera immédiatement ses dates pour tout le monde. Cette action ne peut pas être annulée ici.",
  "calendar.cancelConfirm": "Oui, l'annuler",
  "calendar.cancelled": "Réservation annulée",


  "calendar.clickHintYear": "Cliquez sur un jour de n'importe quel mois pour ajouter une réservation, ou cliquez sur le nom du mois.",
  "calendar.addBooking": "Ajouter une réservation",
  "calendar.bookDay": "Nouvelle réservation le {date}",
  "calendar.bookFor": "Pour",
  "calendar.myself": "Moi-même",
  "calendar.approvedHint": "La réservation est créée comme APPROUVÉE : les dates sont verrouillées immédiatement pour tout le monde.",
  "calendar.bookingAdded": "Réservation ajoutée",
  "calendar.clickHint": "Cliquez sur un jour pour ajouter une réservation — cliquez sur un jour occupé pour voir ses réservations.",
  "calendar.alreadyBooked": "a déjà une réservation sur ces dates",

  // ---- Invitations d'invités ----
  "mod.tabInvitations": "Invitations d'invités",
  "invitations.inviteGuest": "Inviter un invité",
  "invitations.email": "E-mail de l'invité",
  "invitations.firstName": "Prénom",
  "invitations.lastName": "Nom",
  "invitations.send": "Créer l'invitation & copier le lien",
  "invitations.sent": "Invitation créée — lien copié dans le presse-papiers",
  "invitations.linkReady": "Partagez ce lien avec {email} :",
  "invitations.copy": "Copier le lien",
  "invitations.copied": "Lien copié",
  "invitations.sendHint": "L'invité ouvre le lien, choisit un pseudo et un mot de passe, et son compte est activé immédiatement.",
  "invitations.history": "Invitations envoyées",
  "invitations.none": "Aucune invitation envoyée.",
  "invitations.accepted": "Acceptée",
  "invitations.pending": "En attente",
  "invitations.expired": "Expirée",
  "invitations.by": "par",
  "invitations.revoke": "Révoquer l'invitation",
  "invitations.revoked": "Invitation révoquée",
  "invite.title": "Vous êtes invité sur HomeBook",
  "invite.hello": "Bonjour {name} !",
  "invite.username": "Choisissez un pseudo",
  "invite.usernameRules": "3-30 caractères, commence par une lettre (lettres, chiffres, _ ou -).",
  "invite.password": "Choisissez un mot de passe",
  "invite.confirmPassword": "Confirmez le mot de passe",
  "invite.createAccount": "Créer mon compte",
  "invite.welcome": "Bienvenue à bord ! Vous êtes connecté.",
  "invite.invalid": "Cette invitation est invalide, déjà utilisée ou expirée.",
  "invite.passwordMismatch": "Les mots de passe ne correspondent pas",

  // ---- Matrice des permissions ----
  "admin.tabPermissions": "Permissions",
  "permissions.title": "Permissions par rôle",
  "permissions.hint": "Activez les fonctionnalités autorisées pour chaque rôle. Les changements s'appliquent immédiatement à tous les comptes du rôle.",
  "permissions.feature": "Fonctionnalité",
  "permissions.updated": "Permission mise à jour",
  "permissions.feature.expenses.view": "Voir la vue des dépenses",
  "permissions.feature.expenses.manage": "Créer / modifier / supprimer des dépenses",
  "permissions.feature.expenses.payAssigned": "Payer ou annuler les dépenses qui leur sont assignées",
  "permissions.feature.guests.invite": "Inviter des invités (créer les comptes, envoyer les liens)",
  "permissions.feature.users.validate": "Valider les comptes en attente",
  "permissions.feature.families.manage": "Gérer les familles & groupes",
  "permissions.feature.products.manage": "Créer / modifier des biens",
  "permissions.feature.calendar.view": "Voir le calendrier des réservations",
  "permissions.feature.bookings.manage": "Ajouter des réservations depuis le calendrier",
  "settings.currencyTitle": "Devise d'affichage",
  "settings.currencyHint": "Utilisée partout où des prix sont affichés. La modifier rafraîchit tous les montants (pas de conversion des valeurs stockées).",
  "settings.currencySaved": "Devise changée en {currency}",
  "expenses.breakdownTitle": "Répartition fixes / ponctuels",
  "expenses.scopeYear": "Annuel",
  "expenses.scopeMonth": "Mensuel",
  "expenses.fixedTotal": "Fixes",
  "expenses.punctualTotal": "Ponctuels",
  "expenses.periodTotal": "Total",
  "expenses.matrixMonth": "Mois",
  "expenses.chartTitle": "Dépenses par mois",
"expenses.topCategories": "Où va l'argent",
"expenses.dueInDays": "échéance dans {count} jours",
"expenses.dueTomorrow": "échéance demain",
"expenses.dueToday": "échéance aujourd'hui",
  "expenses.allHouses": "Toutes les maisons",
  "expenses.changeAssignment": "Modifier l'assignation",
"expenses.assignmentSaved": "Assignation enregistrée",
"expenses.unassign": "Aucun assigné (effacer)",

  // ---- Notifications ----
  "notif.type.USER_SIGNUP": "Nouvelle inscription",
  "notif.type.USER_VALIDATED": "Compte validé",
  "notif.type.GUEST_INVITED": "Invitation envoyée",
  "notif.type.RESERVATION_REQUEST": "Demande de réservation",
  "notif.type.RESERVATION_APPROVED": "Réservation approuvée",
  "notif.type.RESERVATION_REJECTED": "Réservation refusée",
  "notif.type.BOOKING_ADDED": "Réservation ajoutée",
  "notif.type.EXPENSE_ASSIGNED": "Dépense assignée",
  "notif.type.EXPENSE_PAID": "Dépense payée",
  "notif.type.TEST": "Test",
  "notif.channelsTitle": "Canaux",
  "notif.editEvents": "Modifier les événements",
  "notif.sendTest": "Envoyer un test",
  "notif.testOk": "Test envoyé — vérifiez l'historique et la boîte cible",
  "notif.testNoProvider": "Test enregistré, mais aucun fournisseur actif ne l'a délivré (simulation console)",
  "notif.typesTitle": "Événements suivis",
  "notif.typesHint": "Le canal ne reçoit que les types d'événements sélectionnés. Tout désélectionner = tout recevoir.",
  "notif.allTypes": "Tous les événements",
  "notif.allTypesFallback": "Rien de sélectionné — le canal recevra tous les types d'événements.",
  "notif.filterType": "Type d'événement",
  "notif.all": "Tous",

  // ---- Multi-level navigation + Home screen ----
  "nav.back": "Retour",
  "nav.home": "Accueil",
  "nav.level.browse": "Maisons",
  "nav.level.product": "Maison",
  "nav.level.my-reservations": "Mes séjours",
  "nav.level.host": "Panel propriétaire",
  "nav.level.moderator": "Modération",
  "nav.level.expenses": "Factures",
  "nav.level.dashboard": "Tableau de bord",
  "nav.level.notifications": "Notifications",
  "nav.level.admin": "Console admin",
  "home.welcome": "Bon retour, {name}",
  "home.welcomeGuest": "Bienvenue sur HomeBook",
  "home.subtitle": "Tout à portée de main — choisissez où aller.",
  "home.browse": "Parcourir les maisons",
  "home.browseHint": "Photos, tarifs et calendrier partagé",
  "home.myTrips": "Mes séjours",
  "home.myTripsHint": "Vos demandes, séjours passés et à venir",
  "home.host": "Panel propriétaire",
  "home.hostHint": "Gérez vos maisons, photos et réservations",
  "home.moderator": "À valider",
  "home.moderatorHint": "Réservations à approuver et comptes à valider",
  "home.reviewBadge": "en attente",
  "home.expenses": "Factures",
  "home.expensesHint": "Qui a payé quoi, dettes et factures",
  "home.dashboard": "Tableau de bord",
  "home.dashboardHint": "Totaux par payeur, mois et catégorie",
  "home.notifications": "Notifications",
  "home.notificationsHint": "Canaux d'alerte et historique d'envoi",
  "home.admin": "Console admin",
  "home.adminHint": "Membres, maisons, rôles et réglages",
  "home.docsHint": "Comment tout fonctionne, en mots simples",
  "preview.bannerNote": "Vous consultez l'application en tant qu'un autre membre.",

  // ---- Occupancy, stay members, dashboard rename ----
  "dash.occupancy": "Occupation",
  "dash.occupancyHint": "Nuits occupées par maison et par membre",
  "dash.byHouse": "Nuits par maison",
  "dash.byMember": "Nuits par membre",
  "dash.totalNights": "Nuits réservées",
  "dash.noOccupancy": "Aucun séjour approuvé pour l'instant.",
  "dash.memberSpend": "Part des frais de séjour",
  "dash.spendHint": "La part de chaque membre dans les séjours où il participe",
  "stay.members": "Membres",
  "stay.membersHint": "Tous les membres de ce séjour privé partagent le prix à parts égales.",
  "stay.member": "Membre",
  "stay.addMember": "Ajouter un membre",
  "stay.removeMember": "Retirer",
  "stay.organiser": "Organisateur",
  "stay.share": "Part",
  "stay.each": "{amount} chacun",
  "stay.locked": "Séjour approuvé — les membres sont verrouillés.",
  "stay.lockedPerm": "Vous pouvez encore les modifier (permission : modifier les séjours approuvés).",
  "stay.sharedSingle": "Un séjour partagé est individuel : un seul membre par réservation.",
  "stay.memberAdded": "Membre ajouté au séjour",
  "stay.memberRemoved": "Membre retiré du séjour",
  "stay.count": "{count} membres",
  "stay.people": "{count} personnes",
  "permissions.feature.bookings.editAfterApproval": "Ajouter / retirer des membres sur un séjour privé approuvé",
  "booking.stayMembers": "Qui séjourne ? (privé)",
  "booking.stayMembersHint": "Choisissez les membres qui partagent la maison. Le prix est divisé à parts égales.",
  "booking.you": "Vous",

  // ---- Dashboard filter + money charts ----
  "dash.filterHouse": "Filtrer par maison",
  "dash.allHouses": "Toutes les maisons",
  "dash.expensesPerHouse": "Dépenses par maison",
  "dash.expensesByCategory": "Dépenses par catégorie",
  "dash.paidPerMember": "Payé par membre",
  "dash.owedPerMember": "Dépenses par membre (responsable)",
  "dash.totalBilled": "facturé",
  "dash.noExpenses": "Rien de facturé pour l'instant.",
  "dash.chartsHint": "Chaque chiffre et chaque graphique suit le filtre de maison ci-dessus.",
};
